import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown, Sparkles, CheckCircle2, CreditCard, Tag, Zap, ShieldCheck } from 'lucide-react'
import { Badge, Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { MerchantJourneyStepper } from '@/components/common/merchant-journey-stepper'
import { usePublicPlans } from '@/modules/customer/hooks/use-customer'
import {
  useInitiateCheckoutMutation,
  useVerifySignatureMutation,
} from '@/modules/seller/hooks/use-billing'
import { useAuth } from '@/providers/auth-provider'
import { toast } from 'sonner'

export function ChooseSubscriptionPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: availablePlans, isLoading } = usePublicPlans()

  const checkoutMutation = useInitiateCheckoutMutation()
  const verifyMutation = useVerifySignatureMutation()

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [selectedCycle, setSelectedCycle] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY')

  // Helper to resolve plan price based on chosen billing tenure
  const getPlanPrice = (plan: any) => {
    if (selectedCycle === 'YEARLY' && plan.yearlyPrice) return Number(plan.yearlyPrice)
    if (selectedCycle === 'QUARTERLY' && plan.quarterlyPrice) return Number(plan.quarterlyPrice)
    if (selectedCycle === 'MONTHLY' && plan.monthlyPrice) return Number(plan.monthlyPrice)
    return Number(plan.price)
  }

  const handleCheckout = async (planId: number) => {
    try {
      const checkout = await checkoutMutation.mutateAsync({ planId, couponCode })

      const isSimulated = checkout.razorpaySubscriptionId?.startsWith('sub_sim_')

      // If Razorpay JS SDK script is loaded and not a mock subscription ID, trigger Razorpay Checkout UI Popup
      if (typeof window !== 'undefined' && (window as any).Razorpay && !isSimulated) {
        const options = {
          key: checkout.keyId || 'rzp_test_placeholder',
          subscription_id: checkout.razorpaySubscriptionId,
          name: 'Exotic Autocart Marketplace',
          description: 'Merchant Membership Activation',
          handler: async (response: any) => {
            await verifyMutation.mutateAsync({
              paymentId: response.razorpay_payment_id || `pay_sim_${Date.now()}`,
              subscriptionId: response.razorpay_subscription_id || checkout.razorpaySubscriptionId,
              signature: response.razorpay_signature || 'simulated_valid_signature',
            })
            toast.success('Subscription activated! Redirecting to dashboard...')
            navigate(user?.role === 'DEALER' ? '/dealer' : '/seller', { replace: true })
          },
          prefill: {
            name: user ? `${user.firstName} ${user.lastName}` : '',
            email: user?.email || '',
          },
          theme: { color: '#8b5cf6' },
        }

        const rzp = new (window as any).Razorpay(options)
        rzp.on('payment.failed', function (resp: any) {
          toast.error(resp.error?.description || 'Payment Failed.')
        })
        rzp.open()
      } else {
        // Fallback simulation in local dev if SDK script is offline or using mock IDs
        await verifyMutation.mutateAsync({
          paymentId: `pay_sim_${Date.now()}`,
          subscriptionId: checkout.razorpaySubscriptionId,
          signature: 'simulated_valid_signature',
        })
        toast.success('Subscription activated! Redirecting to dashboard...')
        navigate(user?.role === 'DEALER' ? '/dealer' : '/seller', { replace: true })
      }
    } catch (error) {
      // Handled in mutation onError
    }
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Merchant Journey Stepper */}
      <MerchantJourneyStepper currentStep="CHOOSE_SUBSCRIPTION" role={user?.role} />

      {/* Page Header */}
      <SectionHeading
        eyebrow="Account Activation Required"
        title="Choose Your Subscription Plan"
        description="Your application has been approved! Select a subscription membership tier to activate your merchant account and begin listing."
        align="left"
      />

      {/* Tenure Selection Toggle Tabs */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-xl bg-slate-100 dark:bg-muted p-1 border border-slate-200 dark:border-border/60 shadow-xs">
          {(['MONTHLY', 'QUARTERLY', 'YEARLY'] as const).map((cycle) => (
            <button
              key={cycle}
              type="button"
              onClick={() => setSelectedCycle(cycle)}
              className={`px-4 py-2 rounded-lg text-xs font-extrabold transition-all ${
                selectedCycle === cycle
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-slate-600 dark:text-muted-foreground hover:text-slate-900 dark:hover:text-foreground'
              }`}
            >
              {cycle === 'MONTHLY' ? 'Monthly Billing' : cycle === 'QUARTERLY' ? 'Quarterly (Save 15%)' : 'Yearly (Save 30%)'}
            </button>
          ))}
        </div>
      </div>

      {/* Plans Selection Cards */}
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading subscription plans...</p>
      ) : (availablePlans as any[])?.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(availablePlans as any[]).map((plan: any) => {
            const isSelected = selectedPlanId === plan.id
            const resolvedPrice = getPlanPrice(plan)

            return (
              <Card
                key={plan.id}
                onClick={() => setSelectedPlanId(plan.id)}
                className={`border cursor-pointer transition-all relative overflow-hidden flex flex-col justify-between ${
                  isSelected
                    ? 'border-primary bg-primary/10 shadow-glow ring-2 ring-primary/30'
                    : 'border-slate-200 dark:border-border/80 bg-white dark:bg-card hover:bg-slate-50 dark:hover:bg-muted/50'
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute right-3 top-3">
                    <Badge variant="success" className="gap-1 text-[10px] font-bold">
                      <Sparkles className="size-3" /> Recommended
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold">
                      <Crown className="size-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-bold text-foreground">{plan.name}</h3>
                      <p className="text-[11px] text-muted-foreground">{plan.planType ?? 'MERCHANT'} Tier</p>
                    </div>
                  </div>

                  <div>
                    <p className="font-display text-3xl font-extrabold text-foreground">
                      ₹{resolvedPrice.toLocaleString()}
                      <span className="text-xs font-normal text-muted-foreground">
                        /{selectedCycle.toLowerCase()}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{plan.description}</p>
                  </div>

                  <div className="space-y-2 border-t border-b border-border/60 py-3 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
                      <span>{plan.trialDays > 0 ? `${plan.trialDays} Days Free Trial` : 'Instant Access'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="size-3.5 text-amber-400 shrink-0" />
                      <span>{plan.features?.length || 10} Active Feature Quotas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-3.5 text-primary shrink-0" />
                      <span>Full Lead & Analytics Access</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={checkoutMutation.isPending}
                    className={`w-full font-bold gap-1.5 ${
                      isSelected ? 'bg-primary text-primary-foreground shadow-glow' : 'bg-muted text-foreground hover:bg-primary hover:text-white'
                    }`}
                  >
                    <CreditCard className="size-4" /> {checkoutMutation.isPending && isSelected ? 'Initializing...' : 'Select & Pay via Razorpay'}
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card className="border-border/80 bg-card p-12 text-center text-xs text-muted-foreground space-y-2">
          <Crown className="mx-auto size-10 opacity-30 text-primary" />
          <p className="font-bold text-sm text-foreground">No subscription plans available right now.</p>
          <p>Please contact platform administration.</p>
        </Card>
      )}

      {/* Coupon Code Input Bar */}
      <Card className="border-border/80 bg-card p-4">
        <div className="flex items-center gap-3">
          <Tag className="size-5 text-primary shrink-0" />
          <Input
            placeholder="Have a promo / coupon code? Enter here..."
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            className="text-xs font-mono uppercase bg-background"
          />
        </div>
      </Card>
    </div>
  )
}
